<template>
  <div>
    <transition mode="out-in" name="fade" v-if="!loaded">
      <Loader />
    </transition>
    <transition
      mode="out-in"
      name="fade"
      v-else-if="canDisplay"
      v-visible="displayContent"
    >
      <App />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watchEffect, computed } from "vue";
import { adtag } from "./prefetch";
import Properties from "./properties";
import {
  provideBetData,
  provideBetDataRef
} from "@fresh8/ad-data-composition/src/betData";
import {
  provideBrand,
  provideAdTag
} from "@fresh8/ad-data-composition/src/brand";

import { provideDisplayEvents } from "./composition/displayEvents";
import { useGetBetData } from "./hooks/useGetBetData";

import { awaitPromise } from "./lib/awaitPromise";

import { BetData } from "@fresh8/types/lib/betData";
import { AdTag } from "@fresh8/types/src/index";
import { provideTheme } from "@fresh8/ad-data-composition/src/theme";
import { providePreview } from "@fresh8/ad-data-composition/src/preview";
import { providePreroll } from "./hooks/usePreroll";

// simple wrapper to enable HMR
import App from "./App.vue";
import Loader from "./components/Loader.vue";
import { createJssContext, provideJssContext } from "@fresh8/vue-jss/src";
import {
  provideAnalyticsContext,
  createAnalyticsContext
} from "./lib/analytics";
import { Preview } from "@fresh8/types/src/preview";
import { provideFeatureFlag } from "./hooks/useFeatureFlag";

export default defineComponent({
  name: "AppWrapper",
  components: {
    App,
    Loader
  },
  setup() {
    const displayContent = ref(false);
    const betDataContext = {
      betData: ref<BetData>(),
      get: useGetBetData,
      loading: ref(false)
    };

    const {
      result: adTag,
      loading: adTagLoading,
      error: adTagError
    } = awaitPromise<AdTag>(adtag);

    const {
      result: betResult,
      loading: betDataLoading,
      error: betDataError
    } = useGetBetData();

    watchEffect(() => {
      if (betResult.value) {
        betDataContext.betData.value = betResult.value;

        if (adTag.value && betResult.value.operator?.name) {
          adTag.value.operator = {
            key: betResult.value.operator.name,
            name: betResult.value.operator.friendlyName,
            _id: betResult.value.operator._id
          };
        }

        setTimeout(() => {
          displayContent.value = true;
        }, 1000);
      }
    });

    const jssContext = createJssContext({});

    const brand = computed(() => adTag.value?.brand);
    const preview = computed(() => ({ isPreview: false } as Preview));
    const tracking = computed(() => adTag.value?.tracking);
    const team = computed(() => brand.value?.team);
    const macros = computed(() => adTag.value?.macros);

    provideBetDataRef(betDataContext);
    provideFeatureFlag();
    provideJssContext(jssContext);
    provideBetData(betDataContext.betData, tracking, team, macros);
    provideAdTag(adTag);
    provideBrand(brand);
    providePreview(preview);

    // setup prerolls
    const sizeId = computed(() => adTag.value.size._id);
    const prerollImageSets = computed(() => brand.value?.prerollImageSets);
    const brandPreroll = computed(() => brand.value?.adTemplate.preroll);
    providePreroll(brandPreroll, sizeId, prerollImageSets);

    const analyticsContext = createAnalyticsContext(
      betDataContext.betData,
      adTag
    );

    provideAnalyticsContext(analyticsContext);
    provideDisplayEvents(
      betDataContext.betData,
      computed(() => brand.value.adTemplate.eventCardOrder)
    );
    provideTheme(ref({}));

    watchEffect(() => {
      if (adTagError.value) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      }
    });

    const loaded = computed(
      () => !adTagLoading.value && !betDataLoading.value && !adTagError.value
    );

    watchEffect(() => {
      if (betDataError.value && !adTagError.value) {
        displayContent.value = true;
      }
    });

    return {
      loaded,
      canDisplay: computed(() => adTag.value.brand),
      displayContent,
      debugURL: Properties.debugUri
    };
  }
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
